// Formatters Utility Functions
import { format } from 'date-fns';

export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  if (isNaN(amount)) return currency === 'USD' ? '$0.00' : `0.00 ${currency}`;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatCompactNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 1,
  }).format(num);
};

export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(Math.round(num));
};

export const formatPercentage = (value: string | number, decimals: number = 1): string => {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  return `${(numericValue * 100).toFixed(decimals)}%`;
};

/**
 * Format probability/price into percentage representation (without symbol)
 * Handles small non-zero values and large non-unity values accurately.
 */
export const formatProbability = (price: number, decimals: number = 0): string => {
  const percent = price * 100;

  if (decimals === 0) {
    if (percent >= 0 && percent < 1) return '<1';
    if (percent > 99 && percent < 100) return '>99';
  } else {
    const threshold = 1 / Math.pow(10, decimals);
    if (percent > 0 && percent < threshold) return `<${threshold}`;
  }

  return percent.toFixed(decimals);
};

export const formatDate = (date: string | Date, options?: Intl.DateTimeFormatOptions): string => {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  return new Date(date).toLocaleDateString('en-US', options || defaultOptions);
};

export const formatRelativeTime = (date: string | Date): string => {
  const now = new Date();
  const past = new Date(date);
  const diffMs = now.getTime() - past.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

  return formatDate(date);
};

export const formatVolume = (volume: number | string): string => {
  const vol = typeof volume === 'string' ? parseFloat(volume) : volume;
  if (isNaN(vol)) return '$0';

  if (vol >= 1000000) {
    return `$${Math.round(vol / 1000000)}M`;
  }
  if (vol >= 1000) {
    return `$${(vol / 1000).toFixed(vol >= 100000 ? 0 : 1).replace(/\.0$/, '')}K`;
  }
  return `$${vol.toFixed(2)}`;
};

/**
 * Get abbreviated month name (e.g., 'Nov') from a date string
 */
export const getMonthAbbr = (dateStr?: string): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return format(date, 'MMM');
  } catch (e) {
    return '';
  }
};

/**
 * Get day number (e.g., '30') from a date string
 */
export const getDayNumber = (dateStr?: string): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return format(date, 'd');
  } catch (e) {
    return '';
  }
};

/**
 * Format ended date for resolved markets
 * Example: 'Ended Feb 13, 2026'
 */
export const formatMarketResolutionDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return `Ended ${format(date, 'MMM d, yyyy')}`;
  } catch (e) {
    return '';
  }
};
/**
 * Format future date into "Ends in X hours/days/months"
 */
export const formatTimeRemaining = (date?: string | Date): string => {
  if (!date) return '';
  try {
    const end = new Date(date).getTime();
    const now = new Date().getTime();
    const diffMs = end - now;

    if (diffMs <= 0) return '';

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 30));
    const diffYears = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 365));

    if (diffHours < 24) {
      return `Ends in about ${diffHours} ${diffHours === 1 ? 'hour' : 'hours'}`;
    }
    if (diffDays < 30) {
      return `Ends in ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
    }
    if (diffMonths < 12) {
      return `Ends in ${diffMonths} ${diffMonths === 1 ? 'month' : 'months'}`;
    }
    return `Ends in ${diffYears} ${diffYears === 1 ? 'year' : 'years'}`;
  } catch (e) {
    return '';
  }
};

/**
 * Format a date into a localized time string (e.g., "3:00 AM")
 */
export const formatTime = (date: string | Date): string => {
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'TBD';
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return 'TBD';
  }
};

/**
 * Format a probability/price into cents (e.g., 0.5 -> "50¢")
 */
export const formatSportPrice = (price: any): string => {
  if (price === undefined || price === null) return '';
  const val = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(val) || val < 0) return '';
  return `${(val * 100).toFixed(1)}¢`;
};

export const formatSportSpreadPrice = (price: any): string => {
  if (price === undefined || price === null) return '';
  const val = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(val) || val <= 0) return '';
  return `${(1 - val * 100).toFixed(1)}¢`;
};

/** Converts a raw positionSize string (USDC fixed-point, 6 decimals) → whole share count */
export const formatShares = (positionSize: string): number => {
  return Math.round(Number(positionSize) / 1_000_000);
};
